import { ShowMore, TinyButton, tokens } from 'ComponentLibrary'
import { isEmpty } from 'lodash'
import {
    DEFAULT_THREAD_LIST_PREVIEW_ITEM_COPY,
    ThreadListPreviewItem,
    type ThreadListPreviewItemCopy,
} from 'ComponentLibrary/src/widgets/ThreadListPreview/ThreadListPreviewItem'
import { ThreadWithMetadata } from 'ComponentLibrary/src/common/threads'
import { Stack } from '@mui/material'
import { Typography } from 'ComponentLibrary/src/piazza/text'
import { PlusIcon } from '@phosphor-icons/react'
import { type UserLinkCopy } from 'ComponentLibrary/src/piazza/text/UserLink/UserLink'

type ThreadListPreviewProps<T> = {
    threads?: ThreadWithMetadata<T>[]
    onItemClick: (thread: ThreadWithMetadata<T>) => void
    onNewThreadClick: () => void
    emptyStateText?: string
    copy?: Partial<ThreadListPreviewCopy>
    userLinkCopy?: Partial<UserLinkCopy>
    locale?: string
}

export type ThreadListPreviewCopy = {
    emptyStateText: string
    startThread: string
    showMore: string
    showLess: string
    item: ThreadListPreviewItemCopy
}

const DEFAULT_THREAD_LIST_PREVIEW_COPY: ThreadListPreviewCopy = {
    emptyStateText:
        'Starten Sie eine neue Unterhaltung, um Fragen zu stellen, Unterstützung zu erhalten oder Informationen mit anderen Nutzern auszutauschen.',
    startThread: 'Unterhaltung starten',
    showMore: 'Mehr anzeigen...',
    showLess: 'Weniger anzeigen',
    item: DEFAULT_THREAD_LIST_PREVIEW_ITEM_COPY,
}

export function ThreadListPreview<T>({
    threads,
    onNewThreadClick,
    onItemClick,
    emptyStateText,
    copy,
    userLinkCopy,
    locale = 'de-DE',
}: ThreadListPreviewProps<T>) {
    const resolvedCopy = { ...DEFAULT_THREAD_LIST_PREVIEW_COPY, ...copy }
    const itemCopy = { ...DEFAULT_THREAD_LIST_PREVIEW_COPY.item, ...copy?.item }
    const threadsWithMessages = threads?.filter((thread) => !isEmpty(thread.messages))

    if (isEmpty(threadsWithMessages)) {
        return (
            <Stack direction="column" gap={tokens.SizingSpacing200}>
                <Typography variant="textS" color={tokens.ColorTextDefault}>
                    {emptyStateText || resolvedCopy.emptyStateText}
                </Typography>
                <TinyButton leftIcon={<PlusIcon />} label={resolvedCopy.startThread} onClick={onNewThreadClick} />
            </Stack>
        )
    }

    return (
        <ShowMore threshold={4} gap={4} showMoreLabel={resolvedCopy.showMore} showLessLabel={resolvedCopy.showLess}>
            {threadsWithMessages?.map((thread) => (
                <ThreadListPreviewItem
                    thread={thread}
                    key={thread.uuid}
                    onClick={onItemClick}
                    copy={itemCopy}
                    userLinkCopy={userLinkCopy}
                    locale={locale}
                />
            ))}
        </ShowMore>
    )
}
